import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ThemeProvider } from './lib/theme-provider';
import ErrorBoundary from './components/ErrorBoundary';
import Home from './pages/Home';
import History from './pages/History';
import Schedule from './pages/Schedule';
import ShiftPlanning from './pages/ShiftPlanning';
import Reports from './pages/Reports';
import Guide from './pages/Guide';
import AdminEntryEditor from './pages/admin/EntryEditor';
import ShiftSwap from './pages/ShiftSwap';
import Coverage from './pages/Coverage';
import CoverageRespond from './pages/CoverageRespond';
import OnShift from './pages/OnShift';
import Roster from './pages/Roster';

import Profile from './pages/Profile';
import AdminSettings from './pages/admin/Settings';
import Login from './pages/Login';
import InstallPrompt from './components/InstallPrompt';
import type { ReactNode } from 'react';

/**
 * Wraps a route to require authentication.
 * Redirects to /login if no session, shows loading state while checking.
 * SDD §7.3, FR-AUTH-07
 */
function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'system-ui, sans-serif',
          backgroundColor: '#000000',
          color: '#999999',
        }}
      >
        Loading...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <History />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule"
        element={
          <ProtectedRoute>
            <Schedule />
          </ProtectedRoute>
        }
      />
      {/* Shift Planning Dashboard — all authenticated users can view */}
      <Route
        path="/shift-planning"
        element={
          <ProtectedRoute>
            <ShiftPlanning />
          </ProtectedRoute>
        }
      />
      {/* Legacy officer staffing → redirect to shift planning */}
      <Route path="/officer/staffing" element={<Navigate to="/shift-planning" replace />} />
      {/* Legacy officer submit → redirect to home (submit panel now on home page) */}
      <Route path="/officer/submit" element={<Navigate to="/" replace />} />
      {/* Reports — Sprint 12, FR-REPORT-01 through FR-REPORT-07 */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />
      {/* Quick-Start Guide — Sprint 18, Deliverables 2–4 */}
      <Route
        path="/guide"
        element={
          <ProtectedRoute>
            <Guide />
          </ProtectedRoute>
        }
      />
      {/* Admin pages — consolidated into Settings */}
      <Route path="/admin" element={<Navigate to="/admin/settings" replace />} />
      {/* Legacy admin routes → redirect to settings */}
      <Route path="/admin/on-duty" element={<Navigate to="/admin/settings" replace />} />
      <Route path="/admin/audit" element={<Navigate to="/admin/settings?tab=audit" replace />} />
      <Route path="/admin/holidays" element={<Navigate to="/admin/settings?tab=holidays" replace />} />
      <Route path="/admin/feedback" element={<Navigate to="/admin/settings?tab=feedback" replace />} />
      <Route path="/admin/health" element={<Navigate to="/admin/settings?tab=health" replace />} />
      {/* Admin entry detail with location — Sprint 15, FR-GEO-04 */}
      <Route
        path="/admin/entry/:id"
        element={
          <ProtectedRoute>
            <AdminEntryEditor />
          </ProtectedRoute>
        }
      />
      {/* Shift Swap — Sprint 19, SRS Open Item #3 */}
      <Route
        path="/swaps"
        element={
          <ProtectedRoute>
            <ShiftSwap />
          </ProtectedRoute>
        }
      />
      {/* ShiftCoverage — redirect to combined Swaps & Coverage page */}
      <Route path="/coverage" element={<Navigate to="/swaps" replace />} />
      <Route
        path="/coverage/:id/review"
        element={
          <ProtectedRoute>
            <Coverage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coverage/respond"
        element={
          <ProtectedRoute>
            <CoverageRespond />
          </ProtectedRoute>
        }
      />
      {/* Notification Settings — redirects to profile */}
      <Route path="/notifications" element={<Navigate to="/profile" replace />} />
      {/* Roster — visible to all authenticated members, editable by officers+ */}
      <Route
        path="/roster"
        element={
          <ProtectedRoute>
            <Roster />
          </ProtectedRoute>
        }
      />
      {/* Who's On Duty — visible to all authenticated members */}
      <Route
        path="/on-shift"
        element={
          <ProtectedRoute>
            <OnShift />
          </ProtectedRoute>
        }
      />
      {/* User Profile */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      {/* Admin Settings — system configuration */}
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute>
            <AdminSettings />
          </ProtectedRoute>
        }
      />
      {/* Catch-all → home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <InstallPrompt />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
